"use client";

import type { AnalyticsBrowser } from "@segment/analytics-next";

import { getVisitorIdFromDocumentCookie } from "../visitor-id";
import { enqueueIdentify } from "./analytics-event-queue";
import { getAnalyticsInstance } from "./get-analytics-instance";

/**
 * Identify a user with traits using the canonical visitor id when available.
 *
 * Prefers the middleware-owned `hi_visitor_id` cookie so identify stays aligned
 * with server-side analytics. Falls back to Segment's anonymous id.
 */
async function identifyAnonymous(
  traits?: Record<string, unknown>,
  analytics?: AnalyticsBrowser,
): Promise<void> {
  if (!analytics) {
    analytics = getAnalyticsInstance({ eventType: "identify" });
    if (!analytics) {
      return;
    }

    enqueueIdentify(analytics, traits);
    return;
  }

  const visitorId = getVisitorIdFromDocumentCookie();
  const user = await analytics.user();
  const segmentAnonymousId = user.anonymousId();

  if (visitorId && visitorId !== segmentAnonymousId) {
    void analytics.setAnonymousId(visitorId);
  }

  const anonymousId = visitorId ?? segmentAnonymousId;

  await analytics.identify(anonymousId, traits);
}

export { identifyAnonymous };
